import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { apiAuthAccountDelete, notifyAuthChanged } from '@/entities/auth';
import { clearTokens } from '@/shared/lib/auth';
import { requestFlutterLogout } from '@/shared/lib/bridge';
import { disconnectRealtime } from '@/shared/lib/realtime';

// Deletes the account via DELETE /auth/account (204). On success we tear down
// local session state exactly like useLogout (WS, query cache, tokens, Flutter
// bridge, auth event) and send the user to /login.
export const useDeleteAccount = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation('profile');

  return useMutate<void, void>({
    api: () => apiAuthAccountDelete(),
    onSuccess: () => {
      disconnectRealtime();
      qc.clear();
      clearTokens();
      notifyAuthChanged();
      requestFlutterLogout();
      navigate('/login', { replace: true });
      toast.success(t('deleteAccount.toast.success'));
    },
    onError: (_err, message) => {
      toast.error(message || t('deleteAccount.toast.error'));
    },
  });
};
